import { useUserProfile } from '@/context/UserProfileContext';
import { useAppNavigation } from '@/lib/i18nNavigation';
import { getLocale, isDebugMode } from '@/lib/utils';
import { liveActivityService } from '@/utils/liveActivityService';
import { supabase } from '@/utils/supabase';
import { Capacitor } from '@capacitor/core';
import { IonButton, IonIcon, IonModal, IonToast, useIonRouter } from '@ionic/react';
import { arrowForward } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface GameTrackerProps {
  onPendingGameChange: (gameId: string | null) => void;
}

const GameTracker: React.FC<GameTrackerProps> = ({ onPendingGameChange }) => {
  const { profile } = useUserProfile();
  const [pendingGameId, setPendingGameId] = useState<string | null>(null);
  const [showResumeGameModal, setShowResumeGameModal] = useState(false);
  const [showInviteToast, setShowInviteToast] = useState(false);
  const [inviteGameId, setInviteGameId] = useState<string | null>(null);
  const [gameDetails, setGameDetails] = useState<any>(null);
  const router = useIonRouter();
  const { t } = useTranslation('MGP');
  const { getCurrentPathname } = useAppNavigation();
  const pathname = getCurrentPathname();

  // Subscribe to games when the component mounts
  useEffect(() => {
    if (profile?.userId) {
      setupGameSubscriptions();
    }
    return () => {
      // Clean up subscriptions when component unmounts
      const channels = supabase.getChannels();
      channels.forEach(channel => {
        supabase.removeChannel(channel);
      });
    };
  }, [profile?.userId]);

  // Update parent component when pendingGameId changes
  useEffect(() => {
    onPendingGameChange(pendingGameId);
  }, [pendingGameId, onPendingGameChange]);

  const setupGameSubscriptions = () => {
    if (!profile?.userId) return;

    // First, check for any existing active or pending games
    checkForPendingGames();

    // Subscribe to game_players table for changes related to this user
    supabase
      .channel('game-player-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'game_players',
          filter: `user_id=eq.${profile.userId}`
        },
        (payload: any) => {
          console.log('Game player update received:', payload);
          // When a new game_player record is created for this user, check for game details
          if (payload.eventType === 'INSERT') {
            checkGameDetails(payload.new.game_id);
          }
        }
      )
      .subscribe();

    // Also subscribe to games table for general updates
    supabase
      .channel('game-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'games'
        },
        (payload: any) => {
          console.log('Game update received:', payload);
          // Check if this is a game the user is part of
          checkIfUserInGame(payload.new.id);
        }
      )
      .subscribe();
  };

  // Check game details when we get a new game_player record
  const fetchGameDetails = async (gameId: string) => {
    try {
      if (!gameId) {
        console.error('fetchGameDetails called with invalid gameId:', gameId);
        return null;
      }

      // Get game details with course, club, and player profile information
      const { data, error } = await supabase
        .from('games')
        .select(`
          *,
          course:course_id (
            *,
            club:facility_id (
              *
            )
          ),
          game_players (
            *,
            profile:user_id ( * )
          )
        `)
        .eq('id', gameId)
        .single();

      if (error) {
        console.error(`Supabase error fetching game ${gameId}:`, error.message, error.details, error.hint);
        throw error;
      }

      if (!data) {
        console.warn(`No data returned for game ${gameId}`);
        return null;
      }

      setGameDetails(data);
      return data;
    } catch (error) {
      console.error(`Error fetching game details for ${gameId}:`, error);
      // Don't clear game details on error to prevent UI flashing
      return null;
    }
  };

  const checkGameDetails = async (gameId: string) => {
    try {
      // Get game details
      const gameData = await fetchGameDetails(gameId);

      if (!gameData || !profile?.userId) return;

      // Check if the current user is a player
      const isPlayer = gameData.game_players?.some((player: any) => player.user_id === profile.userId);

      if (isPlayer) {
        // If the game is active and the user is a player, show the resume modal
        if (gameData.status === 'active') {
          const hasSeenModal = localStorage.getItem(`game_resume_${gameId}`) === 'true';
          const isOnGamePage = pathname.includes('/play/');

          if (!hasSeenModal && !isOnGamePage) {
            setPendingGameId(gameId);
            onPendingGameChange(gameId);
            setShowResumeGameModal(true);
          }
        }
        // If the game is pending and the user is a player, they were invited
        else if (gameData.status === 'pending') {
          // Show invitation toast if not already showing
          if (!showInviteToast && !inviteGameId) {
            setInviteGameId(gameId);
            setShowInviteToast(true);
          }
        }
      }
    } catch (error) {
      console.error('Error checking game details:', error);
    }
  };

  // Check if user is part of a specific game
  const checkIfUserInGame = async (gameId: number) => {
    try {
      const { data, error } = await supabase
        .from('game_players')
        .select('*')
        .eq('game_id', gameId)
        .eq('user_id', profile?.userId)
        .single();

      if (error && error.code !== 'PGRST116') return; // PGRST116 is 'no rows returned'

      if (data) {
        // User is part of this game, get game details
        checkGameDetails(gameId.toString());
      }
    } catch (error) {
      console.error('Error checking if user is in game:', error);
    }
  };

  const handleResumeGame = () => {
    if (pendingGameId) {
      // Mark as seen so we don't show the modal again for this game
      localStorage.setItem(`game_resume_${pendingGameId}`, 'true');
      setShowResumeGameModal(false);
      router.push(`/play/${pendingGameId}`);
    }
  };

  // Format date for display
  const formatGameDate = (dateString: string) => {
    const date = new Date(dateString);

    return date.toLocaleDateString(getLocale(), {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleResumeGameLater = () => {
    console.log("close for pendinggame: " + pendingGameId);
    if (pendingGameId) {
      // Mark as seen so we don't show the modal again for this game
      localStorage.setItem(`game_resume_${pendingGameId}`, 'true');
      setShowResumeGameModal(false);
    }
  };

  const handleAcceptInvite = () => {
    if (inviteGameId) {
      setShowInviteToast(false);
      router.push(`/play/${inviteGameId}`);
    }
  };

  const handleDeclineInvite = async () => {
    setShowInviteToast(false);
    // Optionally update the game to remove this player
    if (inviteGameId && profile?.userId) {
      try {
        // Get the current game data
        const { data: gameData } = await supabase
          .from('games')
          .select('players')
          .eq('id', inviteGameId)
          .single();

        if (gameData && gameData.players) {
          // Create a copy of players without this user
          const updatedPlayers = { ...gameData.players };
          delete updatedPlayers[profile.userId];

          // Update the game
          await supabase
            .from('games')
            .update({ players: updatedPlayers })
            .eq('id', inviteGameId);
        }
      } catch (error) {
        console.error('Error declining game invitation:', error);
      }
    }
  };

  // Function to check and update Live Activity for ongoing games
  const checkAndUpdateLiveActivity = async (gameData: any) => {
    if (Capacitor.getPlatform() !== 'ios' || !Capacitor.isNativePlatform()) return;

    try {
      // Check if we already have a Live Activity for this game
      const existingId = await liveActivityService.getActiveActivityId();

      if (existingId && existingId == gameData.id) {
        // Update existing Live Activity
        await liveActivityService.updateLiveActivity(gameData, existingId);
      } else {
        // Start a new Live Activity
        await liveActivityService.startLiveActivity(gameData);
      }
    } catch (error) {
      console.log('Error setting up Live Activity for ongoing game:' + JSON.stringify(error));
    }
  };

  const checkForPendingGames = async () => {
    try {
      if (!profile?.userId) {
        console.log('No user ID found, skipping pending game check');
        return;
      }

      // Query pending games where the user is a player
      const { data, error } = await supabase
        .from('games')
        .select(`
          *,
          game_players!inner(*)
        `)
        .eq('status', 'pending')
        .eq('game_players.user_id', profile.userId)
        .order('updated_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0) {
        const gameData = data[0];
        // Store the first pending game ID
        setPendingGameId(gameData.id);
        onPendingGameChange(gameData.id);

        // Fetch additional game details for display
        fetchGameDetails(gameData.id);

        // If we have a pending game and we're on iOS, try to start/update Live Activity
        checkAndUpdateLiveActivity(data);

        // Check if we should show the resume modal
        const hasSeenModal = localStorage.getItem(`game_resume_${gameData.id}`) === 'true';
        const isOnGamePage = pathname.includes('/play/');

        if (!hasSeenModal && !isOnGamePage) {
          setShowResumeGameModal(true);
        }
      } else {
        setPendingGameId(null);
        onPendingGameChange(null);
        setGameDetails(null);
      }
    } catch (error) {
      console.error('Error checking for pending games:', error);
      setPendingGameId(null);
    }
  };

  return (
    <>
      {/* Resume Game Modal */}
      <IonModal className='auto-height' backdropDismiss={false} handle={false} initialBreakpoint={1} breakpoints={[0, 1]} isOpen={showResumeGameModal} >
        <div className='ion-padding rounded-xl'>
          <h2 className="text-xl font-bold mb-2">{t('resume_game')}</h2>
          <p className="text-muted-foreground mb-6">
            {t('resume_active_game_caption')}
          </p>
          {gameDetails && (
            <div className="bg-muted/20 rounded-lg p-3 mb-safe w-full">
              <div className="flex flex-col justify-between mb-2">
                <span className="font-medium">{gameDetails.course.club.name}</span>
                <span className="font-medium">{gameDetails.course?.course_name}</span>

                <span className="text-sm text-muted-foreground">
                  {formatGameDate(gameDetails.created_at)}
                </span>
              </div>

              {gameDetails.game_players && gameDetails.game_players.length > 0 && (
                <div className="flex flex-wrap">
                  {gameDetails.game_players.map((player: any) => (
                    <div key={player.id} className="px-1 py-1 rounded-full flex items-cente">
                      {player.profile?.imageUrl && (
                        <img src={player.profile.imageUrl} className="size-8 object-cover rounded-full" />
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="gap-3 w-full grid-cols-2 grid">
                <IonButton className='text-sm' expand="block" color="secondary" fill="solid" onClick={handleResumeGameLater}>
                  {t('not_now')}
                </IonButton>
                <IonButton className='text-sm' expand="block" onClick={handleResumeGame}>
                  {t('resume')}
                  <IonIcon icon={arrowForward} />
                </IonButton>
              </div>
            </div>
          )}
        </div>
      </IonModal >

      {/* Game Invitation Toast */}
      < IonToast
        isOpen={showInviteToast}
        message="You've been invited to join a game!"
        position="top"
        buttons={
          [
            {
              text: 'Decline',
              role: 'cancel',
              handler: handleDeclineInvite
            },
            {
              text: 'Join',
              handler: handleAcceptInvite
            }
          ]}
      />
    </>
  );
};

export default GameTracker;
